/**
 * Daily Review Load banner — Wave 1.
 * Shows on Dashboard: adaptive cap, mode (steady / recovery / exam-crunch),
 * progress toward today's goal, and any anti-cramming warning.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { CalendarClock, TrendingUp, AlertTriangle, Flame } from "lucide-react";

const MODE_META = {
  steady:        { color: "bg-emerald-50 text-emerald-700",  label: "Steady pace",  icon: TrendingUp },
  recovery:      { color: "bg-amber-50 text-amber-700",      label: "Recovery mode", icon: AlertTriangle },
  "exam-crunch": { color: "bg-rose-50 text-rose-700",        label: "Exam crunch",   icon: Flame },
};

export default function DailyLoadBanner() {
  const [load, setLoad] = useState(null);

  useEffect(() => {
    api.get("/student/daily-load")
      .then((r) => setLoad(r.data))
      .catch(() => setLoad(null));
  }, []);

  if (!load) return null;
  const meta = MODE_META[load.mode] || MODE_META.steady;
  const Icon = meta.icon;
  const cap = load.recommended_cap || 0;
  const done = load.answered_today || 0;
  const pct = cap > 0 ? Math.min(100, Math.round((done / cap) * 100)) : 0;

  return (
    <Card className="p-4 border-slate-200" data-testid="daily-load-banner">
      <div className="flex items-start gap-3">
        <div className={`h-9 w-9 rounded-lg grid place-items-center shrink-0 ${meta.color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-display font-bold text-sm flex items-center gap-2">
              Today&apos;s review plan
              <span className={`text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full font-semibold ${meta.color}`}>
                {meta.label}
              </span>
            </h3>
            <div className="text-xs text-slate-500 font-medium">
              <span data-testid="daily-load-progress" className="text-slate-700 font-bold">{done}</span>
              <span> / {cap}</span>
              {load.days_to_exam !== null && load.days_to_exam !== undefined && (
                <span className="ml-2 inline-flex items-center gap-1">
                  <CalendarClock className="w-3 h-3" />
                  <span data-testid="days-to-exam">{load.days_to_exam}d to exam</span>
                </span>
              )}
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-2 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                pct >= 100 ? "bg-emerald-500" : pct >= 50 ? "bg-blue-600" : "bg-blue-400"
              }`}
              style={{ width: `${pct}%` }}
              data-testid="daily-load-bar"
            />
          </div>
          {load.warning && (
            <p className="text-[12px] text-slate-700 mt-2 leading-snug" data-testid="daily-load-warning">
              💡 {load.warning}
            </p>
          )}
          {!load.warning && load.recent_retention_7d > 0 && (
            <p className="text-[11px] text-slate-500 mt-1.5">
              7-day recall: <strong>{Math.round(load.recent_retention_7d * 100)}%</strong>
              {load.streak_days > 0 && ` · ${load.streak_days}-day streak`}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}
